import { useMemo } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Clock, ExternalLink, MessageCircle } from 'lucide-react';
import { useClinicBranding } from '../context/ClinicBrandingContext';
import { buildFooterScheduleLines } from '../lib/scheduleDisplay';
import { telHref, waHref } from '../lib/legalIdentity';

export default function ContactPage() {
  const { info, schedule } = useClinicBranding();
  const scheduleLines = useMemo(() => buildFooterScheduleLines(schedule), [schedule]);

  // Paquete legal: sin datos fijos. Teléfono, WhatsApp, correo y domicilio vienen de «Identidad y contacto»
  // (GET /api/clinic-info los superpone); si un dato está vacío, su bloque no se muestra.
  const data = info || {};
  const address = [data.address, data.city].filter((x) => String(x || '').trim()).join(', ');
  const whatsapp = data.whatsapp || data.phone || '';
  const mapUrl = address ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}` : '';

  return (
    <div className="min-h-screen bg-background py-12" data-testid="contact-page">
      <div className="max-w-5xl mx-auto px-4">
        <div className="text-center mb-12">
          <h1 className="font-heading text-4xl md:text-5xl font-bold text-foreground mb-4">Contacto</h1>
          {data.description ? <p className="text-muted-foreground text-lg max-w-2xl mx-auto">{data.description}</p> : null}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="space-y-6">
            <Card className="border border-border">
              <CardContent className="p-6">
                <h2 className="font-heading text-xl font-semibold mb-6">Información de Contacto</h2>
                <div className="space-y-5">
                  {data.phone ? (
                  <a
                    href={telHref(data.phone)}
                    className="flex items-start gap-4 group"
                    data-testid="contact-phone"
                  >
                    <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-primary/20 transition-colors">
                      <Phone className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Teléfono</p>
                      <p className="font-medium text-foreground group-hover:text-primary transition-colors">
                        {data.phone}
                      </p>
                    </div>
                  </a>
                  ) : null}

                  {whatsapp && waHref(whatsapp) ? (
                    <a
                      href={waHref(whatsapp)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-start gap-4 group"
                      data-testid="contact-whatsapp"
                    >
                      <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-primary/20 transition-colors">
                        <MessageCircle className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">WhatsApp de pedidos</p>
                        <p className="font-medium text-foreground group-hover:text-primary transition-colors">{whatsapp}</p>
                      </div>
                    </a>
                  ) : null}

                  {data.email ? (
                  <a
                    href={`mailto:${data.email}`}
                    className="flex items-start gap-4 group"
                    data-testid="contact-email"
                  >
                    <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-primary/20 transition-colors">
                      <Mail className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Email</p>
                      <p className="font-medium text-foreground group-hover:text-primary transition-colors">
                        {data.email}
                      </p>
                    </div>
                  </a>
                  ) : null}

                  {address ? (
                  <a
                    href={mapUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-start gap-4 group"
                    data-testid="contact-address"
                  >
                    <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-primary/20 transition-colors">
                      <MapPin className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Dirección</p>
                      <p className="font-medium text-foreground group-hover:text-primary transition-colors">
                        {address}
                      </p>
                    </div>
                  </a>
                  ) : null}
                </div>
                <p className="mt-6 text-sm text-muted-foreground">
                  Sitio de demostración.{' '}
                  <Link to="/aviso-de-privacidad" className="underline">Aviso de privacidad</Link>
                  {' · '}
                  <Link to="/terminos" className="underline">Términos y condiciones</Link>
                </p>
              </CardContent>
            </Card>

            <Card className="border border-border">
              <CardContent className="p-6">
                <h2 className="font-heading text-xl font-semibold mb-6 flex items-center gap-2">
                  <Clock className="w-5 h-5 text-primary" />
                  Horario de pedidos
                </h2>
                <div className="space-y-2 text-muted-foreground text-sm">
                  {scheduleLines.map((line, i) => (
                    <p key={i} className="leading-relaxed">
                      {line}
                    </p>
                  ))}
                </div>
              </CardContent>
            </Card>

            {address ? (
            <Button asChild className="w-full rounded-full gap-2" data-testid="open-maps-btn">
              <a href={mapUrl} target="_blank" rel="noopener noreferrer">
                <MapPin className="w-4 h-4" />
                Ver en Google Maps
                <ExternalLink className="w-4 h-4" />
              </a>
            </Button>
            ) : null}
          </div>

          {address ? (
          <Card className="border border-border overflow-hidden h-[500px]">
            <iframe
              title={`Ubicación de ${data.name}`}
              src={`https://www.google.com/maps/embed/v1/place?key=AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8&q=${encodeURIComponent(address)}&zoom=15`}
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              data-testid="google-map"
            />
          </Card>
          ) : null}
        </div>
      </div>
    </div>
  );
}
